/**
 * Feedback Dashboard — what people said, and where it got to.
 *
 * Deliberately the Error Log Dashboard's shape, class for class: the same
 * `ea-head` with the crumb and title left and ONE control group pinned right
 * (project picker + collapsible date range), the same `ea-kpis` cards, the same
 * `ea-maintabs` Overview/Records split, the same `ea-card pad` panels with an
 * icon-tile title and a breakdown toggle, and the same `ea-rank` list for a top
 * table. Two operational dashboards that look like two products cost the reader
 * a re-orientation every time they switch.
 *
 * ONE QUERY BEHIND EVERY PANEL. KPIs, both timelines, every breakdown and the
 * top list come from a single server-side aggregation over one filtered set
 * (`/feedback-submissions/dashboard`); the grid runs that same filter through
 * the list endpoint. No two panels can describe different populations.
 *
 * The KPI cards READ, they do not navigate — they are the summary line, and a
 * figure that silently re-filters the screen when brushed is a figure people stop
 * trusting. The things that DO drill are the ones that name a subset: a bar, and
 * a Top-feedback row. Those are honest because the list endpoint takes the same
 * narrowing filters the panels count by (componentType, status, sentiment).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'sharedUi/Calendar';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ListCount } from '../../components/common/list-count';
import ProjectCell from '../../components/common/project-cell';
import ProjectFilter from '../../components/common/project-filter';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import { feedbackSubmissionService } from '../../services/feedback-submission.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import type { FeedbackSubmission } from '../../types/feedback-submission.types';
import { statusSeverity } from './feedback-display';
import SubmissionDetail from './submission-detail';
import { useToastApi } from '../../hooks/use-toast-api';
import '../error-analytics/error-analytics.css';
import './feedback-dashboard.css';

const C = {
  indigo: '#4f46e5',
  violet: '#7c3aed',
  cyan: '#06b6d4',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  slate: '#64748b',
};

const STATUS_COLOR: Record<string, string> = {
  NEW: C.amber,
  IN_REVIEW: C.cyan,
  REVIEWED: C.emerald,
  ACTION_REQUIRED: C.rose,
  CLOSED: C.slate,
};
const SENTIMENT_COLOR: Record<string, string> = {
  POSITIVE: C.emerald,
  NEGATIVE: C.rose,
  UNRATED: C.slate,
};

const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const iso = (d: Date | null) => (d ? new Date(d).toISOString() : undefined);
const dayLabel = (d: string) => {
  const [, m, day] = d.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[Number(m) - 1] ?? ''}`;
};

/** The same count animation the other two dashboards open with. */
function CountUp({ value }: { value: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const dur = 850;
    const step = (now: number) => {
      const p = Math.min((now - t0) / dur, 1);
      setV(value * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{Math.round(v).toLocaleString('en-US')}</>;
}

/** Catmull-Rom → bezier, so the line curves the way the Error timeline's does
 *  rather than reading as a polyline. */
function smooth(points: [number, number][]): string {
  if (points.length < 2) return points.length ? `M${points[0][0]},${points[0][1]}` : '';
  let d = `M${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
  }
  return d;
}

interface Series { name: string; color: string; values: number[] }

/** The timeline: a smooth line per series, or the total when nothing is split. */
function Timeline({ labels, series, total }: { labels: string[]; series: Series[]; total: number[] }) {
  const W = 980;
  const H = 300;
  const L = 48;
  const R = 18;
  const T = 18;
  const B = 34;
  const iw = W - L - R;
  const ih = H - T - B;
  if (!labels.length) return <div className="fb-empty">No feedback in this window.</div>;

  const max = Math.max(1, ...total, ...series.flatMap((s) => s.values));
  const xOf = (i: number) => (labels.length === 1 ? L + iw / 2 : L + (i * iw) / (labels.length - 1));
  const yOf = (v: number) => T + ih - (v / max) * ih;
  const ticks = Math.max(1, Math.round(labels.length / 9));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="fbArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.indigo} stopOpacity="0.3" />
          <stop offset="0.7" stopColor={C.violet} stopOpacity="0.07" />
          <stop offset="1" stopColor={C.violet} stopOpacity="0" />
        </linearGradient>
      </defs>
      {Array.from({ length: 5 }, (_, k) => {
        const v = (max * k) / 4;
        const y = yOf(v);
        return (
          <g key={k}>
            <line className="ea-gl" x1={L} y1={y} x2={W - R} y2={y} />
            <text className="ea-axis" x={L - 10} y={y + 3.5} textAnchor="end">
              {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v)}
            </text>
          </g>
        );
      })}
      {!series.length && (
        <>
          <path
            d={`${smooth(total.map((v, i) => [xOf(i), yOf(v)]))} L${xOf(total.length - 1)},${T + ih} L${xOf(0)},${T + ih} Z`}
            fill="url(#fbArea)"
          />
          <path d={smooth(total.map((v, i) => [xOf(i), yOf(v)]))} fill="none" stroke={C.indigo} strokeWidth="2.8" strokeLinecap="round" />
        </>
      )}
      {series.map((s) => (
        <path
          key={s.name}
          d={smooth(s.values.map((v, i) => [xOf(i), yOf(v)]))}
          fill="none"
          stroke={s.color}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {labels.map((d, i) => ((i % ticks === 0 || i === labels.length - 1) ? (
        <text key={d} className="ea-axis" x={xOf(i)} y={H - 10} textAnchor="middle">{dayLabel(d)}</text>
      ) : null))}
    </svg>
  );
}

/** A bar per category. Clicking one filters the dashboard AND opens Records. */
function BarChart({ items, active, onPick }: {
  items: { label: string; value: number; color: string }[];
  active?: string;
  onPick: (label: string) => void;
}) {
  if (!items.length) return <div className="fb-empty">Nothing to show for this filter.</div>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="fb-bars">
      {items.map((it) => (
        <button
          key={it.label}
          type="button"
          className={`fb-bar ${active === it.label ? 'on' : ''}`}
          onClick={() => onPick(it.label)}
          title={active === it.label ? 'Clear this filter' : `Show ${it.label} records`}
        >
          <span className="fb-bar-lab" title={it.label}>{it.label}</span>
          <span className="fb-bar-track">
            <span className="fb-bar-fill" style={{ width: `${(it.value / max) * 100}%`, background: it.color }} />
          </span>
          <span className="fb-bar-val num">{fmt(it.value)}</span>
        </button>
      ))}
    </div>
  );
}

type MainTab = 'overview' | 'records';
type TlMode = 'sentiment' | 'status';

const FeedbackDashboard = () => {
  const { toast } = useToastApi();
  const { projects } = useAccessibleProjects();

  const [mainTab, setMainTab] = useState<MainTab>('overview');
  const [tlMode, setTlMode] = useState<TlMode>('sentiment');

  const [appFilter, setAppFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [componentType, setComponentType] = useState('');
  const [status, setStatus] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [feedbackKey, setFeedbackKey] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const applyPreset = (days: number | null) => {
    if (days === null) {
      setStartDate(null);
      setEndDate(null);
    } else {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - days);
      setStartDate(from);
      setEndDate(to);
    }
    setPage(1);
  };

  const activePreset = useMemo(() => {
    if (!startDate && !endDate) return 'all';
    if (!startDate || !endDate) return null;
    const days = Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000);
    return days === 7 ? '7d' : days === 30 ? '30d' : null;
  }, [startDate, endDate]);

  const rangeLabel = useMemo(() => {
    if (!startDate && !endDate) return 'All time';
    const f = (d: Date | null) => (d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Any');
    return `${f(startDate)} — ${f(endDate)}`;
  }, [startDate, endDate]);

  const filters = useMemo(() => ({
    dateFrom: iso(startDate),
    dateTo: iso(endDate),
    days: !startDate && !endDate ? 365 : undefined,
    appId: appFilter || undefined,
    componentType: componentType || undefined,
    status: status || undefined,
    sentiment: sentiment || undefined,
  }), [startDate, endDate, appFilter, componentType, status, sentiment]);

  const dashQ = useQuery({
    queryKey: ['feedback-dashboard', filters],
    queryFn: () => feedbackSubmissionService.dashboard(filters),
    staleTime: 30_000,
  });

  const listQ = useQuery({
    queryKey: ['feedback-dashboard-rows', filters, feedbackKey, page, pageSize, search],
    queryFn: () => feedbackSubmissionService.list({
      page,
      page_size: pageSize,
      search: search || undefined,
      feedbackKey: feedbackKey || undefined,
      componentType: filters.componentType,
      status: filters.status,
      sentiment: filters.sentiment,
      appId: filters.appId,
    } as never),
    staleTime: 30_000,
  });

  const refresh = () => { dashQ.refetch(); listQ.refetch(); };

  /** Set a filter and show the rows behind it. Used by the things that name a
   *  SUBSET — a bar, a top-feedback row — never by the KPI cards, which are the
   *  summary line and stay inert. */
  const drill = useCallback((apply: () => void) => {
    apply();
    setPage(1);
    setMainTab('records');
  }, []);

  const toggle = (cur: string, next: string, set: (v: string) => void) => {
    set(cur === next ? '' : next);
    setPage(1);
  };

  const clearAll = () => {
    setComponentType('');
    setStatus('');
    setSentiment('');
    setFeedbackKey('');
    setPage(1);
  };

  const ov = dashQ.data?.overview;
  const rows = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;

  /* ----------------------------------------------------------- timeline data */
  const labels = useMemo(
    () => (dashQ.data?.trend ?? []).map((t) => t.date),
    [dashQ.data],
  );
  const totals = useMemo(() => (dashQ.data?.trend ?? []).map((t) => t.count), [dashQ.data]);
  const series: Series[] = useMemo(() => {
    const src = tlMode === 'sentiment' ? dashQ.data?.trendBySentiment : dashQ.data?.trendByStatus;
    if (!src?.length || !labels.length) return [];
    const colors = tlMode === 'sentiment' ? SENTIMENT_COLOR : STATUS_COLOR;
    const byKey = new Map<string, Map<string, number>>();
    src.forEach((r) => {
      if (!byKey.has(r.key)) byKey.set(r.key, new Map());
      byKey.get(r.key)!.set(r.date, r.count);
    });
    return [...byKey.entries()].map(([name, perDay], i) => ({
      name,
      color: colors[name] || [C.indigo, C.violet, C.cyan, C.amber][i % 4],
      values: labels.map((d) => perDay.get(d) ?? 0),
    }));
  }, [dashQ.data, tlMode, labels]);

  const rangeTotal = totals.reduce((a, b) => a + b, 0);

  /* Names are resolved HERE, not on the server: the aggregation returns actor ids
     and the screen turns them into people, which is how the Audit dashboard's Top
     Users works. Showing the raw id would be the one thing this panel must not do. */
  const topUsers = dashQ.data?.topUsers ?? [];
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!topUsers.length) { setUserNames(new Map()); return; }
    let live = true;
    personnelLookupService
      .getNamesByIds([...new Set(topUsers.map((u) => u.actorId))])
      .then((m) => { if (live) setUserNames(m); })
      .catch(() => { if (live) setUserNames(new Map()); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashQ.data]);

  const initials = (n: string) =>
    (n.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();

  /* --------------------------------------------------------------- KPI cards */
  const kpis: {
    key: string; label: string; val: number; color: string; icon: ReactNode; sub: string;
  }[] = [
    {
      key: 'total',
      label: 'Total feedback',
      val: ov?.total ?? 0,
      color: C.indigo,
      sub: rangeLabel.toLowerCase(),
      icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
    },
    {
      key: 'positive',
      label: 'Positive',
      val: ov?.positive ?? 0,
      color: C.emerald,
      sub: ov?.total ? `${Math.round(((ov.positive ?? 0) / ov.total) * 100)}% of all` : 'none yet',
      icon: <path d="M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />,
    },
    {
      key: 'negative',
      label: 'Negative',
      val: ov?.negative ?? 0,
      color: C.rose,
      sub: ov?.total ? `${Math.round(((ov.negative ?? 0) / ov.total) * 100)}% of all` : 'none yet',
      icon: <path d="M17 14V2M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />,
    },
    {
      key: 'new',
      label: 'Awaiting triage',
      val: ov?.new ?? 0,
      color: C.amber,
      sub: 'reached no inbox yet',
      icon: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4l2.5 2" /></>,
    },
    {
      key: 'reviewed',
      label: 'Reviewed / closed',
      val: (ov?.reviewed ?? 0) + (ov?.closed ?? 0),
      color: C.cyan,
      sub: `of ${ov?.total ?? 0} in this window`,
      icon: <path d="M20 6 9 17l-5-5" />,
    },
  ];

  /* -------------------------------------------------------------------- grid */
  const columns: SaasColumn<FeedbackSubmission>[] = useMemo(() => [
    {
      field: 'feedbackKey',
      header: 'Feedback',
      hideable: false,
      style: { minWidth: 230 },
      body: (d) => (
        <div>
          <div className="font-mono text-xs font-semibold text-ink">{d.feedbackKey || '—'}</div>
          <div className="text-xs text-ink-muted">{d.module || '—'}</div>
        </div>
      ),
    },
    {
      field: 'component',
      header: 'Component',
      style: { minWidth: 160 },
      body: (d) => <Tag value={d.component?.componentType || '—'} severity="info" />,
    },
    {
      field: 'derivedSignals',
      header: 'Sentiment',
      align: 'center',
      style: { width: 130 },
      body: (d) => {
        const s = d.derivedSignals?.sentiment;
        if (!s) return <span className="text-xs text-ink-muted">—</span>;
        return <Tag value={s} severity={s === 'POSITIVE' ? 'success' : 'danger'} />;
      },
    },
    {
      field: 'rating',
      header: 'Rating',
      align: 'center',
      style: { width: 110 },
      body: (d) => {
        const r = d.derivedSignals?.rating;
        const scale = d.derivedSignals?.ratingScale;
        if (r === null || r === undefined) return <span className="text-xs">—</span>;
        return <span className="text-xs">{scale ? `${r} / ${scale}` : r}</span>;
      },
    },
    {
      field: 'status',
      header: 'Status',
      align: 'center',
      style: { width: 150 },
      body: (d) => <Tag value={d.status} severity={statusSeverity(d.status) as never} />,
    },
    {
      field: 'appId',
      header: 'Project',
      style: { minWidth: 150 },
      body: (d) => <ProjectCell appId={(d as { appId?: string | null }).appId} projects={projects} />,
    },
    {
      field: 'submittedAt',
      header: 'Submitted',
      style: { width: 170 },
      body: (d) => (
        <span className="text-xs text-ink-muted">
          {d.submittedAt ? new Date(d.submittedAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      field: 'actions',
      header: '',
      hideable: false,
      align: 'center',
      style: { width: 70 },
      body: (d) => (
        <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="Open" onClick={() => setOpenId(d._id)} />
      ),
    },
  ], [projects]);

  /** Export the FILTERED set, not the page in hand and not the whole collection. */
  const exportExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx-js-style');
      const all = await feedbackSubmissionService.list({
        page: 1,
        page_size: 1000,
        search: search || undefined,
        feedbackKey: feedbackKey || undefined,
        componentType: filters.componentType,
        status: filters.status,
        sentiment: filters.sentiment,
        appId: filters.appId,
      } as never);
      const sheet = XLSX.utils.json_to_sheet((all.items ?? []).map((d) => ({
        Feedback: d.feedbackKey || '',
        Module: d.module || '',
        Component: d.component?.componentType || '',
        Sentiment: d.derivedSignals?.sentiment || '',
        Rating: d.derivedSignals?.rating ?? '',
        Status: d.status,
        Submitted: d.submittedAt ? new Date(d.submittedAt).toLocaleString() : '',
      })));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Feedback');
      XLSX.writeFile(book, `feedback-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  // The detail takes over the tab, as it does on the other feedback screens.
  if (openId) {
    return (
      <>
        {toast}
        <SubmissionDetail submissionId={openId} onBack={() => setOpenId(null)} onChanged={refresh} />
      </>
    );
  }

  const chips = [
    componentType && { k: 'Component', v: componentType, clear: () => toggle(componentType, componentType, setComponentType) },
    status && { k: 'Status', v: status, clear: () => toggle(status, status, setStatus) },
    sentiment && { k: 'Sentiment', v: sentiment, clear: () => toggle(sentiment, sentiment, setSentiment) },
    feedbackKey && { k: 'Feedback', v: feedbackKey, clear: () => toggle(feedbackKey, feedbackKey, setFeedbackKey) },
  ].filter(Boolean) as { k: string; v: string; clear: () => void }[];

  return (
    <div className="ea-root" data-testid="FeedbackDashboard">
      {toast}
      <div className="ea-stack">
        {/* ------------------------------------------------------------- head */}
        <div className="ea-head">
          <div>
            <div className="ea-crumb">
              <b>Feedback Dashboard</b>
              <span className="sep">/</span>
              {rangeLabel}
            </div>
            <h1>Feedback Dashboard</h1>
          </div>
          {/* Project and date range are ONE control group pinned right, exactly as
              on the Error dashboard — `.ea-head` is space-between, so separate
              children would drift into the middle of the header. */}
          <div className="ea-headctl">
            <ProjectFilter value={appFilter} onChange={setAppFilter} testId="FeedbackDashboard.ProjectFilter" />
            <div className="ea-daterange">
              <button
                className={`ea-dr-trigger ${dateOpen ? 'open' : ''}`}
                onClick={() => setDateOpen((o) => !o)}
                aria-expanded={dateOpen}
                aria-label={dateOpen ? 'Hide date range' : 'Change date range'}
                title={dateOpen ? 'Hide date range' : 'Change date range'}
                data-testid="FeedbackDashboard.DateRange"
              >
                {!dateOpen && (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" width="15">
                      <rect x="3" y="5" width="18" height="16" rx="2" />
                      <path d="M8 3v4M16 3v4M3 11h18" />
                    </svg>
                    <span className="l">Date range</span>
                    <b>{rangeLabel}</b>
                  </>
                )}
                <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>

              {dateOpen && (
                <div className="ea-dr-pop">
                  <div className="fields">
                    <div className="fld">
                      <span className="lbl">Start date</span>
                      <Calendar
                        value={startDate}
                        onChange={(e: { value: Date | null }) => { setStartDate(e.value ?? null); setPage(1); }}
                        showIcon
                        showButtonBar
                        dateFormat="dd M yy"
                        placeholder="Any"
                        maxDate={endDate ?? new Date()}
                        appendTo={typeof document !== 'undefined' ? document.body : undefined}
                        testId="FeedbackDashboard.StartDate"
                        style={{ width: 150 }}
                      />
                    </div>
                    <div className="fld">
                      <span className="lbl">End date</span>
                      <Calendar
                        value={endDate}
                        onChange={(e: { value: Date | null }) => { setEndDate(e.value ?? null); setPage(1); }}
                        showIcon
                        showButtonBar
                        dateFormat="dd M yy"
                        placeholder="Any"
                        minDate={startDate ?? undefined}
                        maxDate={new Date()}
                        appendTo={typeof document !== 'undefined' ? document.body : undefined}
                        testId="FeedbackDashboard.EndDate"
                        style={{ width: 150 }}
                      />
                    </div>
                  </div>
                  <div className="presets">
                    <span className="lbl">Quick select</span>
                    <div className="ea-seg">
                      {([['7d', 'Past 7 days', 7], ['30d', 'Past 30 days', 30], ['all', 'All time', null]] as const).map(([key, label, days]) => (
                        <button
                          key={key}
                          className={activePreset === key ? 'on' : ''}
                          onClick={() => applyPreset(days)}
                          data-testid={`FeedbackDashboard.DateRange.${key}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* -------------------------------------------------------------- KPIs */}
        <div className="ea-kpis">
          {kpis.map((k, i) => (
            <div
              key={k.key}
              className="ea-card ea-kpi ea-reveal"
              style={{ animationDelay: `${i * 40}ms`, ['--kc' as string]: k.color, ['--kglow' as string]: `${k.color}99` } as CSSProperties}
            >
              <div className="top">
                <div className="ic" style={{ background: `linear-gradient(135deg, ${k.color}, ${k.color}bb)` }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="17">
                    {k.icon}
                  </svg>
                </div>
                <div className="ea-kpi-meta">
                  <div className="lab">{k.label}</div>
                  <div className="val num"><CountUp value={k.val} /></div>
                </div>
              </div>
              <div className="ea-kpi-src">
                <span className="more" style={{ marginLeft: 'auto' }}>{k.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* -------------------------------------------------------------- tabs */}
        <div className="ea-maintabs">
          <button
            className={mainTab === 'overview' ? 'on' : ''}
            onClick={() => setMainTab('overview')}
            data-testid="FeedbackDashboard.Tab.Overview"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
              <rect x="3" y="3" width="7" height="9" rx="1.5" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" />
            </svg>
            Overview
          </button>
          <button
            className={mainTab === 'records' ? 'on' : ''}
            onClick={() => setMainTab('records')}
            data-testid="FeedbackDashboard.Tab.Records"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M3 9h18M9 4v16" />
            </svg>
            Records<span className="cnt num">{fmt(total)}</span>
          </button>
        </div>

        {mainTab === 'overview' ? (
          <>
            {/* ------------------------------------------------------ timeline */}
            <section className="ea-card pad ea-reveal">
              <div className="ea-card-head">
                <div>
                  <div className="ea-title">
                    <span className="i" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
                        <path d="M3 12h4l2 6 4-14 2 8h6" />
                      </svg>
                    </span>
                    Feedback Timeline
                  </div>
                  <div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>
                    {rangeLabel === 'All time' ? 'Feedback over all time' : `Feedback from ${rangeLabel}`} — by day
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div className="ea-seg" role="group" aria-label="Timeline breakdown">
                    {([['sentiment', 'Sentiment'], ['status', 'Status']] as const).map(([v, l]) => (
                      <button
                        key={v}
                        className={tlMode === v ? 'on' : ''}
                        onClick={() => setTlMode(v)}
                        data-testid={`FeedbackDashboard.Timeline.Mode.${v}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                  <div className="ea-tl-stat">
                    <div className="n num">{fmt(rangeTotal)}</div>
                    <div className="l">feedback · {rangeLabel.toLowerCase()}</div>
                  </div>
                </div>
              </div>
              <Timeline labels={labels} series={series} total={totals} />
              {series.length > 0 && (
                /* `.lg` and `.hint` are what ea-tl-legend styles — the Error
                   timeline's own legend markup, reused rather than restyled. */
                <div className="ea-tl-legend">
                  {series.map((s) => (
                    <span className="lg" key={s.name}>
                      <span className="ea-sw" style={{ background: s.color }} />
                      {s.name}
                    </span>
                  ))}
                  <span className="hint">one line per {tlMode}</span>
                </div>
              )}
            </section>

            {/* ---------------------------------------------------- breakdowns */}
            <div className="fb-chartrow">
              <section className="ea-card pad">
                <div className="ea-card-head">
                  <div>
                    <div className="ea-title">
                      <span className="i" style={{ background: 'linear-gradient(135deg,#06B6D4,#6366F1)' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
                          <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" />
                        </svg>
                      </span>
                      By component type
                    </div>
                    <div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>
                      {componentType ? 'filtered — click again to clear' : 'click a bar to open its records'}
                    </div>
                  </div>
                </div>
                <BarChart
                  items={(dashQ.data?.byComponentType ?? []).map((r, i) => ({
                    label: r.componentType,
                    value: r.count,
                    color: [C.indigo, C.violet, C.cyan, C.emerald, C.amber][i % 5],
                  }))}
                  active={componentType}
                  onPick={(v) => drill(() => toggle(componentType, v, setComponentType))}
                />
              </section>

              <section className="ea-card pad">
                <div className="ea-card-head">
                  <div>
                    <div className="ea-title">
                      <span className="i" style={{ background: 'linear-gradient(135deg,#10B981,#06B6D4)' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
                          <path d="M9 11l3 3L22 4" />
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                      </span>
                      By status
                    </div>
                    <div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>
                      {status ? 'filtered — click again to clear' : 'click a bar to open its records'}
                    </div>
                  </div>
                </div>
                <BarChart
                  items={(dashQ.data?.byStatus ?? []).map((r) => ({
                    label: r.status,
                    value: r.count,
                    color: STATUS_COLOR[r.status] || C.slate,
                  }))}
                  active={status}
                  onPick={(v) => drill(() => toggle(status, v, setStatus))}
                />
              </section>
            </div>

            {/* -------------------------------------------------- top feedback */}
            <section className="ea-card pad">
              <div className="ea-card-head">
                <div>
                  <div className="ea-title">
                    <span className="i" style={{ background: 'linear-gradient(135deg,#F59E0B,#EF4444)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
                        <path d="M4 4h16M4 4v16M8 8h9M8 12h6M8 16h9" />
                      </svg>
                    </span>
                    Top feedback
                  </div>
                  <div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>
                    the definitions people answer most — click one for its records
                  </div>
                </div>
              </div>
              {(dashQ.data?.topFeedback ?? []).length === 0 ? (
                <div className="fb-empty">No feedback in this window.</div>
              ) : (
                <div className="ea-scrolllist">
                  {(dashQ.data?.topFeedback ?? []).map((t, i) => {
                    const max = Math.max(1, ...(dashQ.data?.topFeedback ?? []).map((x) => x.count));
                    return (
                      <div
                        className="ea-rank"
                        key={t.feedbackKey}
                        onClick={() => drill(() => { clearAll(); setFeedbackKey(t.feedbackKey); })}
                        title={`Show records for ${t.feedbackKey}`}
                      >
                        <div className="rk">{String(i + 1).padStart(2, '0')}</div>
                        <div className="body">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                            <span className="code">{t.feedbackKey}</span>
                            <Tag value={t.componentType} severity="info" />
                          </div>
                          <div className="desc">
                            {t.positive} positive · {t.negative} negative
                            {t.averageRating != null ? ` · avg ${t.averageRating}` : ''}
                          </div>
                          <div className="prog">
                            <i style={{ width: `${(t.count / max) * 100}%`, background: C.indigo, animationDelay: `${i * 40}ms` }} />
                          </div>
                        </div>
                        <div className="rt">
                          <div className="oc num">
                            {fmt(t.count)}
                            <small>{t.lastAt ? new Date(t.lastAt).toLocaleDateString() : '—'}</small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ----------------------------------------------------- top users */}
            <section className="ea-card pad ea-reveal">
              <div className="ea-card-head">
                <div className="ea-title">
                  <span className="i" style={{ background: 'linear-gradient(135deg,#14B8A6,#6366F1)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
                    </svg>
                  </span>
                  Top Users
                </div>
                <span className="ea-sub">
                  {topUsers.length} {topUsers.length === 1 ? 'person' : 'people'} · by feedback given
                </span>
              </div>
              {topUsers.length > 0 ? (
                <div className="ea-users">
                  {topUsers.map((u, idx) => {
                    const name = userNames.get(u.actorId) || 'Unknown user';
                    return (
                      <div className="ea-user" key={u.actorId} title={`${name} — ${u.count.toLocaleString()} submissions`}>
                        <span className="rk">#{idx + 1}</span>
                        <span className="ea-avatar" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>{initials(name)}</span>
                        <span className="nm">{name}</span>
                        <span className="cnt num">{u.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="fb-empty">No feedback attributed to a person.</div>
              )}
            </section>
          </>
        ) : (
          /* ------------------------------------------------------------ grid */
          <div className="ea-card fb-gridcard">
            {chips.length > 0 && (
              <div className="ea-activefilters">
                <span className="lbl">Filtered by</span>
                {chips.map((c) => (
                  <button className="ea-fchip" key={c.k} onClick={c.clear}>{c.k}: {c.v} ✕</button>
                ))}
                <button className="ea-fclear" onClick={clearAll}>Clear all</button>
              </div>
            )}
            <SaasDataTable
              tableId="feedback-dashboard"
              columns={columns}
              data={rows}
              loading={listQ.isFetching}
              dataKey="_id"
              emptyMessage="No feedback matches these filters."
              totalRecords={total}
              first={(page - 1) * pageSize}
              rows={pageSize}
              rowsPerPageOptions={[10, 25, 50]}
              onPage={(e: { first: number; rows: number }) => {
                setPageSize(e.rows);
                setPage(Math.floor(e.first / e.rows) + 1);
              }}
              onSort={() => undefined}
              onFilter={() => undefined}
              globalSearch={search}
              onGlobalSearchChange={(v: string) => { setSearch(v); setPage(1); }}
              globalSearchPlaceholder="Search feedback key, component or trace..."
              testId="FeedbackDashboard.Grid"
              toolbarEnd={(
                <>
                  <ListCount
                    total={total}
                    onRefresh={refresh}
                    loading={listQ.isFetching}
                    testId="FeedbackDashboard.Grid.Refresh"
                  />
                  <Button
                    label="Export"
                    icon={exporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                    size="small"
                    outlined
                    onClick={exportExcel}
                    disabled={exporting}
                    data-testid="FeedbackDashboard.Export"
                  />
                </>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDashboard;
